// Georgia I-75 Corridor City Data

export interface GeorgiaCity {
  name: string;
  slug: string;
  county: string;
  state: "GA" | "FL";
  population: string;
  medianHomeValue: string;
  distanceFromHQ: string; // miles from North Port, FL
  i75Position: number; // order along I-75 (1 = northernmost)
  lat: number;
  lng: number;
  faqs: { question: string; answer: string }[];
  whyExpanding: string;
  challenges: string;
}

// Haversine distance in miles between two lat/lng points
function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export const GEORGIA_CITIES: GeorgiaCity[] = [
  // ===== EXISTING 12 CITIES (I-75 Corridor) =====
  {
    name: "Griffin",
    slug: "griffin",
    county: "Spalding County",
    state: "GA",
    population: "23,600",
    medianHomeValue: "$165,000",
    distanceFromHQ: "520",
    i75Position: 1,
    lat: 33.2468,
    lng: -84.2641,
    whyExpanding: "Griffin sits at the northern gateway of our I-75 corridor expansion. As a growing community south of Atlanta, Griffin homeowners deserve access to the same licensed, quality roofing services we've perfected in Florida. Our team brings hurricane-grade installation standards to every project.",
    challenges: "Griffin experiences severe thunderstorms, occasional tornadoes, and significant hail events. The mix of older historic homes and newer construction requires a contractor who understands both modern building codes and legacy roof systems.",
    faqs: [
      { question: "Do you serve Griffin, GA for roofing?", answer: "Yes! O'Brien Contracting is expanding along the I-75 corridor into South Georgia. We bring our Florida-licensed quality and hurricane-grade installation standards to Griffin homeowners." },
      { question: "What roofing materials work best in Griffin?", answer: "Griffin's climate benefits from architectural shingles rated for high winds, standing seam metal roofing for longevity, and impact-resistant options to handle Georgia's hail season." },
      { question: "How does Georgia roofing differ from Florida?", answer: "Georgia has different building codes and climate challenges — more freeze-thaw cycles and hail, less salt air. We adapt our materials and techniques accordingly while maintaining the same quality standards." },
      { question: "Do you offer free estimates in Griffin?", answer: "Absolutely. We provide free, no-obligation roofing estimates for Griffin homeowners. Our team will inspect your roof and provide detailed pricing within 48 hours." }
    ]
  },
  {
    name: "Zebulon",
    slug: "zebulon",
    county: "Pike County",
    state: "GA",
    population: "1,200",
    medianHomeValue: "$145,000",
    distanceFromHQ: "500",
    i75Position: 2,
    lat: 33.1023,
    lng: -84.3429,
    whyExpanding: "Zebulon is a tight-knit community where quality matters. Our family connection to the area — with family living right here in Zebulon — makes this expansion personal. We're bringing proven roofing expertise to Pike County homeowners who deserve reliable, honest service.",
    challenges: "Zebulon's rural properties often feature larger roof spans and older structures. Seasonal storms and occasional hail require durable materials, while the community's character demands a contractor who respects both the homes and the people.",
    faqs: [
      { question: "Why is O'Brien Contracting coming to Zebulon?", answer: "We have family ties in Zebulon and understand the community. We're expanding our proven Florida roofing quality along the I-75 corridor to serve Pike County homeowners with the same dedication." },
      { question: "What services do you offer in Zebulon?", answer: "We offer complete roofing services including metal roofing, shingle installation, roof repairs, inspections, and storm damage restoration for Zebulon properties." },
      { question: "Can you handle rural properties with large roofs?", answer: "Yes. We regularly work on large residential and agricultural properties. Our crews are equipped for projects of all sizes, from small repairs to complete re-roofs on larger structures." },
      { question: "Do you pull permits in Pike County?", answer: "Yes. We handle all permitting requirements for Pike County and ensure every installation meets or exceeds local building codes." }
    ]
  },
  {
    name: "Barnesville",
    slug: "barnesville",
    county: "Lamar County",
    state: "GA",
    population: "6,700",
    medianHomeValue: "$135,000",
    distanceFromHQ: "480",
    i75Position: 3,
    lat: 33.0554,
    lng: -84.1562,
    whyExpanding: "Barnesville is a historic Georgia community that values craftsmanship and reliability. Our expansion brings decades of roofing expertise to Lamar County, ensuring local homeowners have access to top-quality materials and installation without compromising on service.",
    challenges: "Barnesville's historic downtown and older residential neighborhoods feature roofing systems that require careful restoration. The area also sees severe thunderstorms and occasional ice events that stress aging roofs.",
    faqs: [
      { question: "Do you work on historic homes in Barnesville?", answer: "Yes. We have experience with historic properties and can recommend materials that maintain the character of older homes while providing modern protection and code compliance." },
      { question: "What is the cost of a new roof in Barnesville?", answer: "Roof costs in Barnesville typically range from $7,000 to $18,000 depending on size and material. Metal roofs run $12,000-$25,000. We provide free, detailed estimates for accurate pricing." },
      { question: "How quickly can you respond to storm damage?", answer: "We prioritize storm damage calls and can typically have a crew on-site within 48-72 hours for emergency tarping and assessment along our I-75 service corridor." },
      { question: "Are you licensed to work in Georgia?", answer: "O'Brien Contracting holds Florida State License #CCC1335947 and meets all Georgia contractor requirements for the counties we serve along the I-75 corridor." }
    ]
  },
  {
    name: "Forsyth",
    slug: "forsyth",
    county: "Monroe County",
    state: "GA",
    population: "4,300",
    medianHomeValue: "$155,000",
    distanceFromHQ: "460",
    i75Position: 4,
    lat: 33.0343,
    lng: -83.9382,
    whyExpanding: "Forsyth sits at a key interchange on I-75, making it a natural hub for our corridor expansion. Monroe County homeowners gain access to our proven track record of quality roofing installations backed by a Lifetime Workmanship Guarantee.",
    challenges: "Forsyth's location in central Georgia exposes roofs to a full range of weather — from summer thunderstorms and hail to occasional winter ice. The mix of residential and commercial properties requires versatile roofing expertise.",
    faqs: [
      { question: "What types of roofing do you install in Forsyth?", answer: "We install metal roofing (standing seam and corrugated), architectural shingles, flat roofing systems, and specialty materials. All installations include our Lifetime Workmanship Guarantee." },
      { question: "Do you offer financing in Forsyth?", answer: "Yes, we offer flexible financing options to help Forsyth homeowners make their roofing projects affordable. Contact us for current financing terms and options." },
      { question: "How long does a roof replacement take?", answer: "Most residential roofs in Forsyth are completed in 1-3 days. Larger or more complex projects may take up to 5 days. We provide a specific timeline with every estimate." },
      { question: "Do you handle commercial roofing in Monroe County?", answer: "Yes. We provide commercial roofing services including flat roof systems, TPO, and metal roofing for Forsyth businesses and commercial properties." }
    ]
  },
  {
    name: "Macon",
    slug: "macon",
    county: "Bibb County",
    state: "GA",
    population: "157,000",
    medianHomeValue: "$125,000",
    distanceFromHQ: "440",
    i75Position: 5,
    lat: 32.8407,
    lng: -83.6324,
    whyExpanding: "Macon is the largest city on our I-75 corridor and a major hub for central Georgia. With over 157,000 residents, Macon homeowners and businesses need reliable roofing contractors who deliver consistent quality — that's exactly what O'Brien Contracting provides.",
    challenges: "Macon's size and diversity of properties — from historic Ingleside Village homes to modern commercial buildings — require a contractor with broad expertise. The city's humid subtropical climate accelerates roof wear, and severe thunderstorms bring annual hail and wind damage.",
    faqs: [
      { question: "Do you serve all of Macon and Bibb County?", answer: "Yes! We serve all areas of Macon and Bibb County including Ingleside Village, Shirley Hills, Vineville, Wesleyan Woods, and surrounding communities." },
      { question: "What's the best roofing material for Macon homes?", answer: "Metal roofing is increasingly popular in Macon for its durability and energy efficiency. Architectural shingles remain a cost-effective choice. We help homeowners choose based on budget, style, and longevity goals." },
      { question: "Can you work with my insurance company?", answer: "Yes. We have extensive experience with insurance claims for storm damage. We document damage, coordinate with adjusters, and ensure you receive fair compensation for repairs." },
      { question: "Do you offer emergency roofing services in Macon?", answer: "Yes. We provide emergency tarping and temporary repairs for Macon properties affected by storms. Contact us immediately after damage occurs for the fastest response." }
    ]
  },
  {
    name: "Warner Robins",
    slug: "warner-robins",
    county: "Houston County",
    state: "GA",
    population: "80,000",
    medianHomeValue: "$175,000",
    distanceFromHQ: "420",
    i75Position: 6,
    lat: 32.6130,
    lng: -83.6249,
    whyExpanding: "Warner Robins is home to Robins Air Force Base and a thriving community of military families and professionals. These homeowners expect — and deserve — the same quality standards they see on base. O'Brien Contracting delivers that precision and reliability.",
    challenges: "Warner Robins' proximity to Robins AFB means many homes follow military relocation timelines, making roof condition critical for home sales. The area's thunderstorms and occasional tornadoes demand impact-resistant roofing systems.",
    faqs: [
      { question: "Do you offer military discounts in Warner Robins?", answer: "We proudly support our military community. Contact us about available discounts for active duty, veterans, and military families in the Warner Robins area." },
      { question: "Can a new roof help sell my Warner Robins home?", answer: "Absolutely. A new roof is one of the highest-ROI home improvements. For military families on PCS orders, we offer expedited timelines to get your roof done before listing." },
      { question: "What roofing materials are best for Warner Robins?", answer: "Impact-resistant architectural shingles and metal roofing are excellent choices for Warner Robins. Both offer strong wind and hail protection while maintaining curb appeal for the area's well-kept neighborhoods." },
      { question: "How do I get a roofing estimate in Warner Robins?", answer: "Call us or fill out our online form for a free, no-obligation estimate. We'll schedule an inspection and provide detailed pricing within 48 hours." }
    ]
  },
  {
    name: "Perry",
    slug: "perry",
    county: "Houston County",
    state: "GA",
    population: "18,500",
    medianHomeValue: "$195,000",
    distanceFromHQ: "410",
    i75Position: 7,
    lat: 32.4588,
    lng: -83.7316,
    whyExpanding: "Perry is known for the Georgia National Fairgrounds and a rapidly growing residential market. New construction and established neighborhoods both benefit from our expertise in durable, energy-efficient roofing systems designed to last decades.",
    challenges: "Perry's growth means a mix of newer construction needing warranty-backed installations and older homes requiring re-roofing. Severe weather events, including straight-line winds from summer thunderstorms, are a significant concern.",
    faqs: [
      { question: "Do you work on new construction in Perry?", answer: "Yes. We partner with builders and homeowners on new construction roofing in Perry. We install all major material types and ensure every new roof meets current Georgia building codes." },
      { question: "What is your warranty on Perry roofing jobs?", answer: "We provide a Lifetime Workmanship Guarantee on all installations, plus manufacturer warranties ranging from 25-50 years depending on the material selected." },
      { question: "How do Perry building codes affect roofing?", answer: "Houston County has specific wind and impact requirements. We pull all required permits and ensure full code compliance on every Perry roofing project." },
      { question: "Can you replace my roof during fair season?", answer: "Yes! We schedule around your needs. During the Georgia National Fair season, we coordinate timing to minimize disruption to Perry homeowners and businesses." }
    ]
  },
  {
    name: "Cordele",
    slug: "cordele",
    county: "Crisp County",
    state: "GA",
    population: "10,700",
    medianHomeValue: "$95,000",
    distanceFromHQ: "370",
    i75Position: 8,
    lat: 31.9635,
    lng: -83.7741,
    whyExpanding: "Cordele, the 'Watermelon Capital of the World,' is a hardworking community that values honest service and fair pricing. Our expansion into Crisp County brings affordable, quality roofing options backed by our Lifetime Workmanship Guarantee.",
    challenges: "Cordele's position in south-central Georgia means exposure to both Gulf-originating storms and inland severe weather. Many properties have aging roofs that need replacement, and affordable options are essential for the local market.",
    faqs: [
      { question: "Is roofing affordable in Cordele?", answer: "We work with every budget. Cordele homeowners can take advantage of our financing options and competitive pricing. Shingle roofs start around $6,000, and we'll find the best solution for your budget." },
      { question: "Do you handle storm damage in Crisp County?", answer: "Yes. We respond to storm damage calls throughout Crisp County, providing emergency tarping, damage documentation, and insurance claim assistance." },
      { question: "What's the most durable roof for Cordele's climate?", answer: "Metal roofing offers the best long-term value in Cordele — 50+ year lifespan, excellent wind resistance, and energy savings. It's an investment that pays for itself over time." },
      { question: "How far south on I-75 do you work?", answer: "We serve the entire I-75 corridor from Griffin, GA down to Lake City, FL. Cordele is centrally located on our route, making service convenient and responsive." }
    ]
  },
  {
    name: "Tifton",
    slug: "tifton",
    county: "Tift County",
    state: "GA",
    population: "17,500",
    medianHomeValue: "$145,000",
    distanceFromHQ: "330",
    i75Position: 9,
    lat: 31.4505,
    lng: -83.5085,
    whyExpanding: "Tifton is a vibrant agricultural and university community. Home to Abraham Baldwin Agricultural College, Tifton combines academic energy with rural charm. Our roofing services bring professional-grade quality to Tift County's residential and commercial properties.",
    challenges: "Tifton's south Georgia location means intense summer heat, frequent thunderstorms, and the tail end of tropical systems. Roofs need UV-resistant materials and proper ventilation to handle the extreme heat and humidity.",
    faqs: [
      { question: "Do you offer commercial roofing in Tifton?", answer: "Yes. We serve Tifton businesses with commercial roofing solutions including flat roof systems, TPO, metal panels, and maintenance programs for commercial properties." },
      { question: "How does Tifton's heat affect roofing?", answer: "Tifton's intense summer heat (95°F+) accelerates shingle aging and can warp improperly installed materials. We use heat-resistant underlayments and ensure proper attic ventilation on every installation." },
      { question: "What energy-efficient options are available?", answer: "Metal roofing with reflective coatings can reduce cooling costs by 20-30% in Tifton's climate. We also offer radiant barrier underlayments and ridge vent systems for maximum efficiency." },
      { question: "Do you serve areas around Tifton?", answer: "Yes! We serve all of Tift County and surrounding areas including Omega, Ty Ty, and nearby communities along the I-75 corridor." }
    ]
  },
  {
    name: "Valdosta",
    slug: "valdosta",
    county: "Lowndes County",
    state: "GA",
    population: "56,500",
    medianHomeValue: "$160,000",
    distanceFromHQ: "280",
    i75Position: 10,
    lat: 30.8327,
    lng: -83.2785,
    whyExpanding: "Valdosta is the largest city in south Georgia and our key hub near the Florida border. Home to Valdosta State University and Moody Air Force Base, the city's diverse housing stock — from student rentals to military family homes to established neighborhoods — needs reliable roofing services.",
    challenges: "Valdosta sits in the crosshairs of tropical systems moving inland from both the Gulf and Atlantic. The city sees more annual rainfall than most of Georgia, putting extra stress on roof drainage and waterproofing systems.",
    faqs: [
      { question: "Is Valdosta your main Georgia service area?", answer: "Valdosta is one of our primary hubs along the I-75 corridor. Its proximity to our Florida operations makes it ideal for responsive service throughout Lowndes County." },
      { question: "Do you work with Moody AFB housing?", answer: "Yes. We serve military families in and around Moody Air Force Base with quality roofing installations. Ask about our military appreciation pricing." },
      { question: "What about Valdosta's heavy rainfall?", answer: "Valdosta averages 50+ inches of rain annually. We install enhanced drainage systems, premium underlayments, and ensure proper slope and flashing to handle heavy rainfall." },
      { question: "Can you handle rental property roofing?", answer: "Yes. We work with landlords and property managers on both repairs and replacements for rental properties, including multi-family units common near VSU campus." }
    ]
  },
  {
    name: "Lake Park",
    slug: "lake-park",
    county: "Lowndes County",
    state: "GA",
    population: "800",
    medianHomeValue: "$130,000",
    distanceFromHQ: "265",
    i75Position: 11,
    lat: 30.6866,
    lng: -83.1810,
    whyExpanding: "Lake Park sits right at the Georgia-Florida border, making it a natural bridge between our Florida and Georgia operations. This small community deserves the same quality roofing services available in larger cities, and we're here to provide it.",
    challenges: "Lake Park's border location means it gets the worst of both Florida hurricanes tracking north and Georgia severe weather moving south. Properties here need roofing systems that can handle the full spectrum of southeastern weather challenges.",
    faqs: [
      { question: "Why serve such a small town?", answer: "Every homeowner deserves quality roofing. Lake Park is on our I-75 route between Florida and Georgia operations, making it easy for our crews to serve the community without compromising response times." },
      { question: "Do Florida or Georgia codes apply in Lake Park?", answer: "Georgia building codes apply in Lake Park. However, our Florida hurricane-grade installation standards often exceed Georgia requirements, giving Lake Park homeowners extra protection." },
      { question: "What's the best roof for Lake Park's weather?", answer: "Metal roofing is ideal for Lake Park — it handles both tropical moisture from the south and severe thunderstorms from the north. Architectural shingles rated for 130+ mph winds are also excellent." },
      { question: "How quickly can you get to Lake Park?", answer: "Lake Park is strategically located on our I-75 corridor. We can typically schedule inspections within a few days and begin work within 1-2 weeks of approval." }
    ]
  },
  {
    name: "Lake City",
    slug: "lake-city",
    county: "Columbia County",
    state: "FL",
    population: "12,500",
    medianHomeValue: "$150,000",
    distanceFromHQ: "240",
    i75Position: 12,
    lat: 30.1897,
    lng: -82.6393,
    whyExpanding: "Lake City is the southern anchor of our I-75 corridor expansion and the first Florida city on the route from Georgia. As a Florida community, Lake City homeowners benefit from our deep expertise in Florida building codes, hurricane preparation, and insurance claim assistance.",
    challenges: "Lake City faces the full force of Florida's hurricane season while also experiencing more freeze events than South Florida. This dual climate challenge requires materials and installation methods that handle both extremes.",
    faqs: [
      { question: "Is Lake City covered under your Florida license?", answer: "Yes! Lake City is in Florida, and our Florida State License #CCC1335947 fully covers all roofing work in Columbia County. We pull all required permits and ensure full Florida Building Code compliance." },
      { question: "Does the My Safe Florida Home program apply here?", answer: "Yes! Lake City homeowners may qualify for up to $10,000 through the My Safe Florida Home program for roof hardening. We're a qualified contractor for approved projects." },
      { question: "What's different about North Florida roofing?", answer: "Lake City experiences occasional freezing temperatures unlike South Florida. We select materials rated for freeze-thaw cycles while still meeting Florida's stringent hurricane wind codes." },
      { question: "Do you handle insurance claims in Lake City?", answer: "Yes. We have extensive experience with Florida insurance claims, including documentation, adjuster meetings, and supplement filing to ensure Columbia County homeowners receive fair compensation." }
    ]
  },

  // ===== NEW CITIES — Central Georgia =====
  {
    name: "Jackson",
    slug: "jackson",
    county: "Butts County",
    state: "GA",
    population: "5,100",
    medianHomeValue: "$155,000",
    distanceFromHQ: "510",
    i75Position: 13,
    lat: 33.2946,
    lng: -83.9660,
    whyExpanding: "Jackson is a growing Butts County community conveniently located between Atlanta and Macon on I-75. As development pushes south from the metro area, Jackson homeowners need quality roofing contractors who understand both new construction standards and legacy home restoration.",
    challenges: "Jackson's position in the Georgia Piedmont means exposure to severe thunderstorms, occasional tornadoes, and winter ice storms. The area's red clay soil also creates drainage challenges around foundations that can affect roof runoff systems.",
    faqs: [
      { question: "Do you provide roofing services in Jackson, GA?", answer: "Yes! Jackson is part of our I-75 corridor expansion. We provide full roofing services including metal, shingle, and flat roof installations with our Lifetime Workmanship Guarantee." },
      { question: "What roofing materials are popular in Jackson?", answer: "Architectural shingles and standing seam metal are the most popular choices in Jackson. Both handle Georgia's severe weather well and offer excellent curb appeal." },
      { question: "Do you offer financing for Jackson homeowners?", answer: "Yes. We offer flexible financing options to help Jackson homeowners afford quality roofing. Contact us for current rates and terms." },
    ]
  },
  {
    name: "Thomaston",
    slug: "thomaston",
    county: "Upson County",
    state: "GA",
    population: "9,200",
    medianHomeValue: "$110,000",
    distanceFromHQ: "470",
    i75Position: 14,
    lat: 32.8882,
    lng: -84.3266,
    whyExpanding: "Thomaston is a proud mill town with a strong sense of community. Our expansion into Upson County brings reliable, affordable roofing services to homeowners who value quality craftsmanship and honest pricing — core principles that drive our business.",
    challenges: "Thomaston's older housing stock includes many homes built during the textile era that need careful roof restoration. Severe thunderstorms and hail are common in the spring and summer months, making impact-resistant materials essential.",
    faqs: [
      { question: "Can you work on older homes in Thomaston?", answer: "Absolutely. We specialize in working with older homes, ensuring structural integrity while upgrading to modern, durable roofing materials." },
      { question: "What's the most affordable roofing option?", answer: "Architectural shingles provide the best value in Thomaston, starting around $380/square. We also offer financing to spread costs over time." },
      { question: "How do I schedule an estimate in Thomaston?", answer: "Call us or fill out our online form. We'll schedule a free inspection and have a detailed estimate to you within 48 hours." },
    ]
  },
  {
    name: "Fort Valley",
    slug: "fort-valley",
    county: "Peach County",
    state: "GA",
    population: "8,800",
    medianHomeValue: "$105,000",
    distanceFromHQ: "430",
    i75Position: 15,
    lat: 32.5535,
    lng: -83.8874,
    whyExpanding: "Fort Valley, the 'Peach Capital of Georgia,' is home to Fort Valley State University and a community proud of its agricultural heritage. Our roofing services bring professional-grade quality to Peach County at prices that work for the local market.",
    challenges: "Fort Valley's hot, humid climate accelerates roof material degradation. Summer temperatures regularly exceed 95°F, and afternoon thunderstorms bring heavy rain and occasional hail that test even well-installed roofs.",
    faqs: [
      { question: "Do you serve Fort Valley and Peach County?", answer: "Yes! Fort Valley is part of our central Georgia service area. We provide residential and commercial roofing throughout Peach County." },
      { question: "What's the best roof for Fort Valley's heat?", answer: "Metal roofing with reflective coatings reduces cooling costs significantly in Fort Valley's climate. We also ensure proper ventilation for every installation." },
      { question: "Do you work with insurance companies?", answer: "Yes. We assist Fort Valley homeowners with storm damage claims, including documentation, adjuster coordination, and fair settlement advocacy." },
    ]
  },
  {
    name: "Vienna",
    slug: "vienna",
    county: "Dooly County",
    state: "GA",
    population: "4,000",
    medianHomeValue: "$85,000",
    distanceFromHQ: "400",
    i75Position: 16,
    lat: 32.0914,
    lng: -83.7956,
    whyExpanding: "Vienna is a small but resilient Dooly County community where neighbors look out for each other. We're expanding here because every homeowner — regardless of town size — deserves access to quality roofing services at fair prices.",
    challenges: "Vienna's rural location means many properties have aging roofs that have weathered years of Georgia storms. Affordable, durable solutions are critical for this community, and metal roofing offers the best long-term value.",
    faqs: [
      { question: "Is Vienna too small for your service area?", answer: "Not at all. We serve communities of all sizes along our corridor. Vienna is directly on our route, making service efficient and responsive." },
      { question: "What are the most affordable options for Vienna?", answer: "Shingle roofing starts around $380/square, and we offer financing. Metal roofing, while higher upfront, pays for itself in longevity and energy savings." },
      { question: "Can you handle agricultural buildings?", answer: "Yes. We install metal roofing on barns, equipment shelters, and agricultural structures throughout Dooly County." },
    ]
  },
  {
    name: "Hawkinsville",
    slug: "hawkinsville",
    county: "Pulaski County",
    state: "GA",
    population: "5,300",
    medianHomeValue: "$95,000",
    distanceFromHQ: "400",
    i75Position: 17,
    lat: 32.2838,
    lng: -83.4721,
    whyExpanding: "Hawkinsville, known for its harness racing heritage, is a Pulaski County gem. Our expansion brings the same quality roofing services we've built our reputation on to this welcoming community along the Ocmulgee River.",
    challenges: "Hawkinsville's riverfront location means higher humidity and moisture exposure for roofs. Spring flooding events and severe thunderstorms add stress to roofing systems, making waterproofing and proper drainage essential.",
    faqs: [
      { question: "Do you serve Hawkinsville?", answer: "Yes! Hawkinsville is within our Georgia service area. We offer free estimates and full roofing services for Pulaski County homeowners." },
      { question: "Is moisture a concern for Hawkinsville roofs?", answer: "Yes. The Ocmulgee River proximity increases humidity. We use moisture-barrier underlayments and ensure proper ventilation to prevent mold and rot." },
      { question: "What warranty do you offer?", answer: "Every installation includes our Lifetime Workmanship Guarantee plus manufacturer warranties of 25-50 years depending on material." },
    ]
  },
  {
    name: "Eastman",
    slug: "eastman",
    county: "Dodge County",
    state: "GA",
    population: "5,100",
    medianHomeValue: "$90,000",
    distanceFromHQ: "380",
    i75Position: 18,
    lat: 32.1974,
    lng: -83.1793,
    whyExpanding: "Eastman is Dodge County's seat and a community that values hard work and reliability. Our expansion into this area brings professional roofing services backed by decades of experience to homeowners who expect honest work at fair prices.",
    challenges: "Eastman's central Georgia location puts it in the path of severe weather from both Gulf storms and inland systems. Older homes dominate the housing stock, and many need roof upgrades to meet current wind resistance standards.",
    faqs: [
      { question: "What services do you offer in Eastman?", answer: "We provide complete roofing services: metal, shingle, and flat roof installation, repairs, inspections, emergency service, and insurance claim assistance." },
      { question: "How do I know if I need a new roof?", answer: "Signs include missing shingles, leaks, sagging, granule loss, or a roof over 20 years old. We offer free inspections to assess your roof's condition." },
      { question: "Do you offer emergency repairs in Dodge County?", answer: "Yes. We respond to emergency calls with tarping and temporary repairs to protect your home until permanent work can be scheduled." },
    ]
  },
  {
    name: "Cochran",
    slug: "cochran",
    county: "Bleckley County",
    state: "GA",
    population: "5,200",
    medianHomeValue: "$100,000",
    distanceFromHQ: "390",
    i75Position: 19,
    lat: 32.3871,
    lng: -83.3549,
    whyExpanding: "Cochran is home to Middle Georgia State University's campus and a growing community that blends academic life with rural Georgia charm. Our roofing services bring big-city quality to Bleckley County without big-city prices.",
    challenges: "Cochran's mix of student housing, faculty homes, and established residential areas requires versatile roofing solutions. Severe storms and high summer heat demand materials that perform under both extremes.",
    faqs: [
      { question: "Do you serve the Cochran area?", answer: "Yes! Cochran is part of our central Georgia service area. We serve residential and commercial properties throughout Bleckley County." },
      { question: "Can you work on multi-family housing?", answer: "Yes. We handle multi-family roofing projects including apartments and townhomes, common near the university campus." },
      { question: "What's the timeline for a roof replacement?", answer: "Most Cochran residential roofs are completed in 1-3 days. We provide a specific timeline with every estimate." },
    ]
  },

  // ===== NEW CITIES — Southwest Georgia =====
  {
    name: "Columbus",
    slug: "columbus",
    county: "Muscogee County",
    state: "GA",
    population: "206,000",
    medianHomeValue: "$155,000",
    distanceFromHQ: "480",
    i75Position: 20,
    lat: 32.4610,
    lng: -84.9877,
    whyExpanding: "Columbus is Georgia's third-largest city and home to Fort Moore (formerly Fort Benning). With over 206,000 residents and a massive military community, Columbus needs reliable roofing contractors who can handle projects of every scale — from base housing to historic Uptown buildings.",
    challenges: "Columbus's diverse architecture spans over 200 years, from antebellum homes along Broadway to modern subdivisions near Fort Moore. The Chattahoochee River corridor creates unique microclimate conditions, and severe thunderstorms bring damaging hail and wind each spring.",
    faqs: [
      { question: "Do you serve all of Columbus and Muscogee County?", answer: "Yes! We serve all areas of Columbus including Uptown, Midtown, Bibb City, Green Island Hills, and the Fort Moore corridor." },
      { question: "Do you offer military discounts?", answer: "Yes. We proudly support our Fort Moore military community with special pricing for active duty, veterans, and military families." },
      { question: "Can you handle commercial roofing in Columbus?", answer: "Absolutely. We serve Columbus businesses with commercial flat roofing, TPO, metal panels, and maintenance programs." },
      { question: "What about Columbus's historic homes?", answer: "We have experience restoring roofs on historic properties while maintaining architectural character and meeting modern code requirements." }
    ]
  },
  {
    name: "Americus",
    slug: "americus",
    county: "Sumter County",
    state: "GA",
    population: "15,800",
    medianHomeValue: "$110,000",
    distanceFromHQ: "420",
    i75Position: 21,
    lat: 32.0724,
    lng: -84.2327,
    whyExpanding: "Americus is best known as the home of Habitat for Humanity International and Georgia Southwestern State University. This community that pioneered affordable housing deserves affordable, high-quality roofing services — and that's exactly what we deliver.",
    challenges: "Americus was devastated by an EF-3 tornado in 2007 and understands the importance of storm-resistant construction. The area sees frequent severe weather, making impact-resistant roofing materials and proper installation methods critical.",
    faqs: [
      { question: "Do you serve Americus and Sumter County?", answer: "Yes! Americus is within our expanded Georgia service area. We offer full residential and commercial roofing services throughout Sumter County." },
      { question: "What storm-resistant options do you offer?", answer: "We install impact-resistant shingles, standing seam metal roofing, and fortified roof systems designed to withstand severe Georgia storms." },
      { question: "Do you work with Habitat for Humanity?", answer: "We support the affordable housing mission. Contact us about partnership opportunities for community roofing projects in the Americus area." },
    ]
  },
  {
    name: "Albany",
    slug: "albany",
    county: "Dougherty County",
    state: "GA",
    population: "69,000",
    medianHomeValue: "$105,000",
    distanceFromHQ: "380",
    i75Position: 22,
    lat: 31.5785,
    lng: -84.1557,
    whyExpanding: "Albany is southwest Georgia's largest city and a regional hub for commerce, healthcare, and education. With nearly 70,000 residents, Albany's diverse housing market — from Radium Springs estates to Westover neighborhoods — needs dependable roofing services.",
    challenges: "Albany has been hit by multiple devastating storms, including the 2017 tornado outbreak and frequent flooding from the Flint River. This storm history makes roof resilience a top priority for local homeowners and businesses.",
    faqs: [
      { question: "Do you serve Albany and Dougherty County?", answer: "Yes! Albany is a key city in our southwest Georgia expansion. We serve all neighborhoods in Dougherty County with full roofing services." },
      { question: "How do you handle Albany's storm damage history?", answer: "We install fortified roofing systems designed for Albany's severe weather. Our storm damage restoration includes insurance claim assistance and emergency tarping." },
      { question: "What are the most popular roofing materials in Albany?", answer: "Metal roofing is growing rapidly in Albany due to its storm resistance and energy efficiency. Architectural shingles remain the most affordable option." },
      { question: "Do you offer free estimates in Albany?", answer: "Yes. We provide free, no-obligation estimates for all Albany and Dougherty County properties." }
    ]
  },
  {
    name: "Dawson",
    slug: "dawson",
    county: "Terrell County",
    state: "GA",
    population: "4,400",
    medianHomeValue: "$75,000",
    distanceFromHQ: "400",
    i75Position: 23,
    lat: 31.7732,
    lng: -84.4474,
    whyExpanding: "Dawson is Terrell County's seat and a community where affordability matters. We bring quality roofing services at competitive prices because every homeowner — regardless of home value — deserves a roof that protects their family.",
    challenges: "Dawson's rural properties often have larger footprints and aging roofs. Limited local contractor options have left many homeowners without access to licensed, warranty-backed roofing services until now.",
    faqs: [
      { question: "Do you serve Dawson, GA?", answer: "Yes! Dawson is part of our southwest Georgia expansion. We offer free estimates and full roofing services throughout Terrell County." },
      { question: "What's the most budget-friendly option?", answer: "Architectural shingles start around $380/square in Dawson. We also offer financing options to make any roofing project affordable." },
      { question: "Can you handle rural properties?", answer: "Absolutely. We work on properties of all sizes, including farm buildings, larger rural homes, and commercial structures." },
    ]
  },
  {
    name: "Leesburg",
    slug: "leesburg",
    county: "Lee County",
    state: "GA",
    population: "3,200",
    medianHomeValue: "$185,000",
    distanceFromHQ: "370",
    i75Position: 24,
    lat: 31.7310,
    lng: -84.1710,
    whyExpanding: "Leesburg is one of Lee County's fastest-growing communities, with new subdivisions and rising home values. These newer homes deserve quality roofing installations that protect both the investment and the family inside.",
    challenges: "Leesburg's rapid growth means many homes are under 20 years old with builder-grade roofs approaching their first replacement cycle. Choosing the right upgrade material is critical for long-term value in this market.",
    faqs: [
      { question: "Do you serve Leesburg and Lee County?", answer: "Yes! Leesburg is within our southwest Georgia service area. We offer complete roofing services for residential and commercial properties." },
      { question: "When should I replace my builder-grade roof?", answer: "Most builder-grade shingle roofs last 15-20 years in Georgia's climate. If your Leesburg home is approaching that age, schedule a free inspection to assess its condition." },
      { question: "What upgrade options do you recommend?", answer: "For Leesburg's newer homes, we recommend upgrading from builder-grade to premium architectural shingles or metal roofing for better durability and curb appeal." },
    ]
  },

  // ===== NEW CITIES — South Georgia =====
  {
    name: "Ashburn",
    slug: "ashburn",
    county: "Turner County",
    state: "GA",
    population: "3,900",
    medianHomeValue: "$85,000",
    distanceFromHQ: "350",
    i75Position: 25,
    lat: 31.7066,
    lng: -83.6533,
    whyExpanding: "Ashburn sits directly on I-75, making it one of the most accessible cities on our corridor. This Turner County community benefits from our efficient routing and competitive pricing, bringing professional roofing to rural south Georgia.",
    challenges: "Ashburn's exposure to severe thunderstorms and occasional tornadoes makes roof integrity critical. Many properties have older roofs that have endured decades of Georgia weather without professional maintenance.",
    faqs: [
      { question: "Do you serve Ashburn?", answer: "Yes! Ashburn is directly on our I-75 route, making it one of the easiest cities for us to serve efficiently." },
      { question: "What's your response time for Ashburn?", answer: "Being on I-75, we can typically schedule inspections within a few days and start work within 1-2 weeks." },
      { question: "Do you offer metal roofing in Ashburn?", answer: "Yes. Metal roofing is an excellent choice for Ashburn's climate — durable, energy-efficient, and built to last 50+ years." },
    ]
  },
  {
    name: "Fitzgerald",
    slug: "fitzgerald",
    county: "Ben Hill County",
    state: "GA",
    population: "9,000",
    medianHomeValue: "$95,000",
    distanceFromHQ: "330",
    i75Position: 26,
    lat: 31.7149,
    lng: -83.2527,
    whyExpanding: "Fitzgerald — the 'Colony City' — has a unique Northern-Southern heritage and a community that values hard work. Our expansion into Ben Hill County brings professional roofing services to a city that's been underserved by licensed contractors.",
    challenges: "Fitzgerald's location in south-central Georgia means it catches severe weather from multiple directions. The city's older housing stock needs careful assessment and often requires complete re-roofing rather than patching.",
    faqs: [
      { question: "Do you serve Fitzgerald and Ben Hill County?", answer: "Yes! Fitzgerald is part of our south Georgia expansion. We provide complete roofing services with free estimates." },
      { question: "What about Fitzgerald's older homes?", answer: "We specialize in re-roofing older properties. We assess structural integrity and recommend the best materials for long-lasting protection." },
      { question: "Do you handle commercial properties?", answer: "Yes. We serve Fitzgerald businesses with flat roof, TPO, and metal roofing installations and repairs." },
    ]
  },
  {
    name: "Sylvester",
    slug: "sylvester",
    county: "Worth County",
    state: "GA",
    population: "5,600",
    medianHomeValue: "$90,000",
    distanceFromHQ: "340",
    i75Position: 27,
    lat: 31.5310,
    lng: -83.8366,
    whyExpanding: "Sylvester is Worth County's vibrant seat, known for its peanut farming heritage and community spirit. We're expanding here to bring licensed, warranty-backed roofing services to an area where quality contractors have been hard to find.",
    challenges: "Sylvester's agricultural economy means many properties include both residential and farm structures. Spring severe weather, including hail and straight-line winds, regularly damages roofs across the county.",
    faqs: [
      { question: "Do you serve Sylvester?", answer: "Yes! We serve Sylvester and all of Worth County with residential, commercial, and agricultural roofing services." },
      { question: "Can you roof farm buildings?", answer: "Absolutely. We install metal roofing on barns, equipment shelters, and agricultural structures of all sizes." },
      { question: "What's the best value roofing material?", answer: "For Sylvester's climate and budget, architectural shingles offer the best short-term value. Metal roofing is the best long-term investment." },
    ]
  },
  {
    name: "Moultrie",
    slug: "moultrie",
    county: "Colquitt County",
    state: "GA",
    population: "14,500",
    medianHomeValue: "$120,000",
    distanceFromHQ: "310",
    i75Position: 28,
    lat: 31.1799,
    lng: -83.7891,
    whyExpanding: "Moultrie is Colquitt County's thriving seat and the 'Sunbelt Agricultural Exposition' host city. With nearly 15,000 residents and a growing economy, Moultrie homeowners deserve professional roofing services backed by real warranties.",
    challenges: "Moultrie's south Georgia climate brings extreme heat, heavy rainfall, and severe thunderstorms. The city's mix of historic downtown buildings and suburban homes requires versatile roofing expertise.",
    faqs: [
      { question: "Do you serve Moultrie and Colquitt County?", answer: "Yes! Moultrie is a key city in our south Georgia expansion. We serve all residential and commercial properties in Colquitt County." },
      { question: "What roofing works best for Moultrie's heat?", answer: "Metal roofing with reflective coatings is ideal for Moultrie's hot summers, reducing cooling costs by up to 30%. Cool-color shingles are another energy-efficient option." },
      { question: "Do you offer storm damage repair?", answer: "Yes. We provide emergency tarping, damage documentation, and insurance claim assistance for storm-damaged Moultrie properties." },
    ]
  },
  {
    name: "Adel",
    slug: "adel",
    county: "Cook County",
    state: "GA",
    population: "5,500",
    medianHomeValue: "$100,000",
    distanceFromHQ: "300",
    i75Position: 29,
    lat: 31.1372,
    lng: -83.4236,
    whyExpanding: "Adel is Cook County's seat and a welcoming south Georgia community on the I-75 corridor. Our expansion here brings professional roofing services to homeowners who've had limited access to licensed, warranty-backed contractors.",
    challenges: "Adel's proximity to both the Gulf and Atlantic makes it vulnerable to tropical systems. The city's hot, humid climate also promotes moss and algae growth on roofs, requiring materials with built-in protection.",
    faqs: [
      { question: "Do you serve Adel?", answer: "Yes! Adel is on our I-75 corridor and part of our south Georgia service area. We offer free estimates for all Cook County properties." },
      { question: "How do you prevent algae on Adel roofs?", answer: "We install algae-resistant shingles with copper granules and metal roofing that naturally resists biological growth in Adel's humid climate." },
      { question: "What's the cost of a new roof in Adel?", answer: "Typical Adel homes range from $6,500-$16,000 for shingles and $12,000-$25,000 for metal. We provide exact pricing with our free estimates." },
    ]
  },
  {
    name: "Nashville",
    slug: "nashville-ga",
    county: "Berrien County",
    state: "GA",
    population: "5,000",
    medianHomeValue: "$95,000",
    distanceFromHQ: "300",
    i75Position: 30,
    lat: 31.2074,
    lng: -83.2502,
    whyExpanding: "Nashville is Berrien County's seat and a community rooted in south Georgia's agricultural traditions. Our roofing services bring licensed expertise and real warranties to homeowners who deserve better options than what's locally available.",
    challenges: "Nashville's rural setting means many homes sit on large lots with mature tree canopy that drops debris on roofs. Combined with severe weather, regular maintenance and durable materials are essential.",
    faqs: [
      { question: "Do you serve Nashville, GA?", answer: "Yes! Nashville, GA is part of our south Georgia service area. We serve residential and commercial properties throughout Berrien County." },
      { question: "How do trees affect my roof?", answer: "Tree debris accelerates shingle wear and traps moisture. We recommend regular maintenance and can install metal roofing that handles debris better than shingles." },
      { question: "Do you offer free inspections?", answer: "Yes. We provide free roof inspections and estimates for Nashville homeowners. Call us or submit a form to schedule." },
    ]
  },
  {
    name: "Camilla",
    slug: "camilla",
    county: "Mitchell County",
    state: "GA",
    population: "5,100",
    medianHomeValue: "$80,000",
    distanceFromHQ: "360",
    i75Position: 31,
    lat: 31.2312,
    lng: -84.2102,
    whyExpanding: "Camilla is Mitchell County's seat and a tight-knit community in southwest Georgia. Our expansion brings affordable, professional roofing to homeowners who need reliable service and honest pricing.",
    challenges: "Camilla was hit by a devastating tornado in 2000, and the community understands storm preparedness. Impact-resistant roofing materials and proper installation are priorities for local homeowners.",
    faqs: [
      { question: "Do you serve Camilla?", answer: "Yes! Camilla is part of our southwest Georgia expansion. We offer complete roofing services with free estimates." },
      { question: "What storm-resistant options do you offer?", answer: "We install impact-resistant shingles, standing seam metal roofing, and fortified roof systems designed for severe Georgia weather." },
      { question: "Are your prices competitive for Camilla?", answer: "Yes. We price competitively for the southwest Georgia market while maintaining the quality standards we're known for." },
    ]
  },
  {
    name: "Thomasville",
    slug: "thomasville",
    county: "Thomas County",
    state: "GA",
    population: "18,500",
    medianHomeValue: "$145,000",
    distanceFromHQ: "320",
    i75Position: 32,
    lat: 30.8366,
    lng: -83.9786,
    whyExpanding: "Thomasville is renowned for its Victorian architecture, rose gardens, and historic plantation homes. This Thomas County gem deserves roofing contractors who understand how to preserve historic character while providing modern protection.",
    challenges: "Thomasville's treasure trove of Victorian and antebellum homes requires specialized roofing knowledge. Modern materials must complement historic aesthetics while meeting current building codes and weather standards.",
    faqs: [
      { question: "Do you work on Thomasville's historic homes?", answer: "Yes! We have experience with historic properties and select materials that complement Victorian, Craftsman, and antebellum architectural styles." },
      { question: "What about Thomasville's newer neighborhoods?", answer: "We serve all of Thomas County, from historic downtown to new subdivisions. Every installation gets our Lifetime Workmanship Guarantee." },
      { question: "Is Thomasville in your service area?", answer: "Yes! Thomasville is a key city in our south Georgia expansion, approximately 320 miles from our North Port headquarters." },
      { question: "Do you handle commercial roofing in Thomasville?", answer: "Yes. We serve Thomasville businesses with commercial flat roofing, TPO, metal, and maintenance programs." }
    ]
  },
  {
    name: "Bainbridge",
    slug: "bainbridge",
    county: "Decatur County",
    state: "GA",
    population: "12,500",
    medianHomeValue: "$115,000",
    distanceFromHQ: "350",
    i75Position: 33,
    lat: 30.9038,
    lng: -84.5755,
    whyExpanding: "Bainbridge sits on beautiful Lake Seminole and is Decatur County's thriving seat. This southwest Georgia community combines lakefront living with small-town values — and now has access to our professional roofing services.",
    challenges: "Bainbridge's lakefront properties face unique challenges including higher humidity, algae growth, and exposure to storms crossing the Apalachicola River basin. Waterfront homes need superior waterproofing and corrosion-resistant materials.",
    faqs: [
      { question: "Do you serve Bainbridge and Lake Seminole?", answer: "Yes! Bainbridge is part of our southwest Georgia expansion. We serve all of Decatur County including lakefront properties." },
      { question: "What's best for waterfront homes?", answer: "Metal roofing is ideal for lakefront properties — corrosion-resistant, algae-proof, and built to handle high humidity environments." },
      { question: "Do you serve Lake Seminole vacation homes?", answer: "Yes. We work on both primary residences and vacation properties around Lake Seminole and throughout Decatur County." },
    ]
  },
  {
    name: "Cairo",
    slug: "cairo-ga",
    county: "Grady County",
    state: "GA",
    population: "9,800",
    medianHomeValue: "$100,000",
    distanceFromHQ: "310",
    i75Position: 34,
    lat: 30.8774,
    lng: -84.2052,
    whyExpanding: "Cairo (pronounced 'KAY-ro') is Grady County's seat and a thriving south Georgia agricultural community. Our expansion brings licensed, warranty-backed roofing to homeowners who value quality work and honest pricing.",
    challenges: "Cairo's position near the Florida border means it sees tropical weather influences earlier than cities further north. The combination of extreme heat and storm exposure demands roofing materials built for the harshest conditions.",
    faqs: [
      { question: "Do you serve Cairo, GA?", answer: "Yes! Cairo is part of our south Georgia expansion. We provide full roofing services throughout Grady County." },
      { question: "How is Cairo's climate different?", answer: "Cairo's near-Florida location means hotter summers and more tropical storm influence. We use materials rated for these conditions — the same grade we use in Florida." },
      { question: "Do you offer financing in Cairo?", answer: "Yes. We offer flexible financing to help Cairo homeowners afford quality roofing. Contact us for current rates and terms." },
    ]
  },
  {
    name: "Quitman",
    slug: "quitman",
    county: "Brooks County",
    state: "GA",
    population: "3,700",
    medianHomeValue: "$85,000",
    distanceFromHQ: "290",
    i75Position: 35,
    lat: 30.7849,
    lng: -83.5597,
    whyExpanding: "Quitman is Brooks County's seat and a gateway community near the Florida line. Our expansion into this area means local homeowners finally have access to a licensed, insured roofing contractor with a proven track record.",
    challenges: "Quitman's proximity to Florida means it faces similar weather challenges — tropical storms, extreme heat, and heavy rainfall. But without Florida's strict building codes, many older homes have roofs that wouldn't meet hurricane standards.",
    faqs: [
      { question: "Do you serve Quitman?", answer: "Yes! Quitman is within our south Georgia service corridor. We offer free estimates for all Brooks County properties." },
      { question: "Do you install hurricane-grade roofs in Georgia?", answer: "Yes. Even though Georgia doesn't require it, we can install Florida hurricane-grade roofing systems for Quitman homeowners who want maximum protection." },
      { question: "What's the cost range for Quitman?", answer: "Typical Quitman homes range from $6,000-$15,000 for shingles and $11,000-$24,000 for metal. We provide exact pricing with free estimates." },
    ]
  },
  {
    name: "Donalsonville",
    slug: "donalsonville",
    county: "Seminole County",
    state: "GA",
    population: "2,700",
    medianHomeValue: "$75,000",
    distanceFromHQ: "380",
    i75Position: 36,
    lat: 31.0404,
    lng: -84.8793,
    whyExpanding: "Donalsonville is Georgia's southwestern corner — Seminole County's seat near Lake Seminole. This small community has been underserved by quality roofing contractors, and our expansion changes that.",
    challenges: "Donalsonville's position at the Georgia-Florida-Alabama tri-state area means exposure to Gulf storms. The rural setting and limited contractor options have left many homeowners without access to professional roofing services.",
    faqs: [
      { question: "Do you come all the way to Donalsonville?", answer: "Yes! Donalsonville is part of our expanding Georgia service area. We schedule efficient route-based visits to serve Seminole County homeowners." },
      { question: "What materials work best here?", answer: "Metal roofing is the top choice for Donalsonville — longest lifespan, best storm resistance, and lowest maintenance for rural properties." },
      { question: "How do I schedule an estimate?", answer: "Call us or fill out our online form. We schedule Donalsonville visits on our southwest Georgia route and provide estimates within 48 hours." },
    ]
  },
  {
    name: "Blakely",
    slug: "blakely",
    county: "Early County",
    state: "GA",
    population: "4,800",
    medianHomeValue: "$80,000",
    distanceFromHQ: "390",
    i75Position: 37,
    lat: 31.3777,
    lng: -84.9341,
    whyExpanding: "Blakely, the 'Peanut Capital of the World,' is Early County's proud seat. Our expansion into this agricultural community brings professional, warranty-backed roofing services that match the work ethic of this farming region.",
    challenges: "Blakely's rural properties include both residential homes and agricultural structures that need durable roofing. Spring severe weather and occasional tornadoes make impact-resistant materials a priority.",
    faqs: [
      { question: "Do you serve Blakely and Early County?", answer: "Yes! Blakely is part of our southwest Georgia expansion. We offer residential, commercial, and agricultural roofing services." },
      { question: "Can you roof peanut storage buildings?", answer: "Yes. We install metal roofing on agricultural and commercial structures of all sizes throughout Early County." },
      { question: "What's the most durable option for Blakely?", answer: "Standing seam metal roofing offers the best durability for Blakely's climate — 50+ year lifespan with minimal maintenance." },
    ]
  },

  // ===== NEW CITIES — Smaller County Seats =====
  {
    name: "Butler",
    slug: "butler",
    county: "Taylor County",
    state: "GA",
    population: "1,800",
    medianHomeValue: "$70,000",
    distanceFromHQ: "430",
    i75Position: 38,
    lat: 32.5568,
    lng: -84.2384,
    whyExpanding: "Butler is Taylor County's small but spirited seat. Every community, no matter the size, deserves access to licensed roofing professionals. Our corridor expansion makes that possible for Butler homeowners.",
    challenges: "Butler's older housing stock includes many homes that need complete re-roofing. Limited local options have meant homeowners often settle for subpar repairs. We bring professional standards to every project.",
    faqs: [
      { question: "Is Butler too small to be in your service area?", answer: "Not at all. We serve communities of all sizes. Butler is on our route through central Georgia, making service efficient." },
      { question: "What's the most affordable option?", answer: "Shingle roofing is the most affordable starting point. We also offer financing to help Butler homeowners manage costs." },
      { question: "Do you handle emergency repairs?", answer: "Yes. We provide emergency tarping and temporary repairs to protect Butler homes after storms." },
    ]
  },
  {
    name: "Abbeville",
    slug: "abbeville",
    county: "Wilcox County",
    state: "GA",
    population: "2,800",
    medianHomeValue: "$75,000",
    distanceFromHQ: "360",
    i75Position: 39,
    lat: 31.9922,
    lng: -83.3069,
    whyExpanding: "Abbeville is Wilcox County's seat, sitting along the Ocmulgee River. Our expansion brings professional roofing services to a community that has had limited access to licensed contractors with real warranties.",
    challenges: "Abbeville's riverfront location increases humidity and moisture exposure. Older homes along the river corridor need careful roof assessment and moisture-resistant materials to prevent structural damage.",
    faqs: [
      { question: "Do you serve Abbeville?", answer: "Yes! Abbeville is part of our south Georgia service area. We offer free estimates for all Wilcox County properties." },
      { question: "How does river proximity affect roofing?", answer: "Higher humidity promotes moss, algae, and moisture damage. We use moisture-barrier underlayments and algae-resistant materials for Abbeville homes." },
      { question: "What warranty do you provide?", answer: "Every installation includes our Lifetime Workmanship Guarantee plus manufacturer warranties of 25-50 years." },
    ]
  },
  {
    name: "Ocilla",
    slug: "ocilla",
    county: "Irwin County",
    state: "GA",
    population: "3,400",
    medianHomeValue: "$80,000",
    distanceFromHQ: "330",
    i75Position: 40,
    lat: 31.5943,
    lng: -83.2481,
    whyExpanding: "Ocilla is Irwin County's seat and the 'Sweet Potato Capital of Georgia.' This agricultural community deserves roofing services that match its values — honest work, fair prices, and lasting quality.",
    challenges: "Ocilla's south Georgia climate brings intense heat and severe storms. Many properties have aging roofs that need full replacement rather than continued patching.",
    faqs: [
      { question: "Do you serve Ocilla?", answer: "Yes! Ocilla is part of our south Georgia expansion. We provide complete roofing services with free estimates throughout Irwin County." },
      { question: "What's the best long-term investment?", answer: "Metal roofing offers the best return in Ocilla — 50+ year lifespan, energy savings, and minimal maintenance compared to shingles." },
      { question: "Can you help with storm damage claims?", answer: "Yes. We document storm damage, coordinate with insurance adjusters, and ensure Ocilla homeowners receive fair claim settlements." },
    ]
  },

  // ===== NEW CITIES — East Georgia Expansion =====
  {
    name: "Savannah",
    slug: "savannah",
    county: "Chatham County",
    state: "GA",
    population: "147,000",
    medianHomeValue: "$280,000",
    distanceFromHQ: "450",
    i75Position: 41,
    lat: 32.0809,
    lng: -81.0912,
    whyExpanding: "Savannah is Georgia's oldest city and a major coastal hub with 147,000 residents. Its mix of historic squares, Victorian architecture, and modern development creates enormous demand for skilled roofing contractors who understand coastal building requirements and historic preservation.",
    challenges: "Savannah's coastal location means direct hurricane exposure, salt air corrosion, and intense humidity. Historic district regulations add complexity to roofing projects, requiring materials that meet preservation standards while providing modern storm protection.",
    faqs: [
      { question: "Do you serve Savannah and Chatham County?", answer: "Yes! Savannah is a key market in our statewide Georgia expansion. We serve all neighborhoods including the Historic District, Midtown, Southside, and surrounding Chatham County." },
      { question: "Can you work on Savannah's historic homes?", answer: "Yes. We have experience with historic properties and can recommend approved materials that maintain architectural character while providing modern protection and code compliance." },
      { question: "How do you handle coastal weather in Savannah?", answer: "We use corrosion-resistant materials, hurricane-rated fasteners, and enhanced underlayment systems designed for Savannah's salt air and storm exposure." },
      { question: "Do you offer free estimates in Savannah?", answer: "Yes. We provide free, no-obligation estimates for all Savannah and Chatham County properties. Call or submit our online form to schedule." }
    ]
  },
  {
    name: "Augusta",
    slug: "augusta",
    county: "Richmond County",
    state: "GA",
    population: "202,000",
    medianHomeValue: "$165,000",
    distanceFromHQ: "520",
    i75Position: 42,
    lat: 33.4735,
    lng: -81.9748,
    whyExpanding: "Augusta is Georgia's second-largest city, home to the Masters Tournament and a thriving military community at Fort Eisenhower. With over 202,000 residents, Augusta's housing market demands reliable roofing contractors who deliver consistent quality across diverse neighborhoods.",
    challenges: "Augusta's position along the Savannah River creates unique microclimate conditions with high humidity. Severe thunderstorms, occasional ice storms, and intense summer heat challenge roofing systems year-round.",
    faqs: [
      { question: "Do you serve all of Augusta and Richmond County?", answer: "Yes! We serve all areas of Augusta including Summerville, National Hills, West Augusta, Martinez, and Evans in neighboring Columbia County." },
      { question: "Do you offer military discounts for Fort Eisenhower?", answer: "Yes. We support our military community with special pricing for active duty, veterans, and military families stationed at Fort Eisenhower." },
      { question: "What roofing materials work best in Augusta?", answer: "Metal roofing and impact-resistant architectural shingles perform excellently in Augusta's climate, handling both summer heat and occasional winter ice events." },
      { question: "How quickly can you start a project in Augusta?", answer: "We typically schedule inspections within a few days and can begin work within 1-2 weeks of estimate approval." }
    ]
  },
  {
    name: "Athens",
    slug: "athens",
    county: "Clarke County",
    state: "GA",
    population: "127,000",
    medianHomeValue: "$245,000",
    distanceFromHQ: "560",
    i75Position: 43,
    lat: 33.9519,
    lng: -83.3576,
    whyExpanding: "Athens is home to the University of Georgia and a vibrant community of 127,000. The mix of student housing, faculty homes, historic neighborhoods, and commercial properties creates year-round demand for quality roofing services at every price point.",
    challenges: "Athens' diverse housing stock ranges from UGA-area rentals to upscale Five Points homes. Severe thunderstorms and occasional tornadoes are the primary weather threats, while the area's clay soil and tree canopy create debris and drainage challenges.",
    faqs: [
      { question: "Do you serve Athens and Clarke County?", answer: "Yes! Athens is part of our expanded Georgia coverage. We serve all areas including Five Points, Normaltown, Boulevard, and surrounding Oconee County." },
      { question: "Do you work on rental properties near UGA?", answer: "Yes. We work with landlords and property managers on repairs and replacements for rental properties, including multi-family units near campus." },
      { question: "What's the best roofing for Athens' climate?", answer: "Architectural shingles rated for high winds and standing seam metal are the top choices for Athens. Both handle severe storms well and offer excellent curb appeal." },
      { question: "Do you pull permits in Clarke County?", answer: "Yes. We handle all permitting and ensure every installation meets or exceeds local building codes." }
    ]
  },
  {
    name: "Statesboro",
    slug: "statesboro",
    county: "Bulloch County",
    state: "GA",
    population: "34,000",
    medianHomeValue: "$185,000",
    distanceFromHQ: "430",
    i75Position: 44,
    lat: 32.4488,
    lng: -81.7832,
    whyExpanding: "Statesboro is home to Georgia Southern University and a growing southeast Georgia community. The university brings steady housing demand, and our expansion gives Bulloch County homeowners access to professional-grade roofing at competitive prices.",
    challenges: "Statesboro's inland southeast Georgia location means severe thunderstorms and occasional tropical remnants. High heat and humidity accelerate roof material degradation, making proper ventilation and UV-resistant materials essential.",
    faqs: [
      { question: "Do you serve Statesboro?", answer: "Yes! Statesboro is within our expanded Georgia service area. We offer full roofing services throughout Bulloch County." },
      { question: "What about student housing roofing?", answer: "We work with landlords and property management companies on multi-family and rental property roofing near Georgia Southern campus." },
      { question: "What's the timeline for a roof replacement?", answer: "Most Statesboro residential roofs are completed in 1-3 days. We provide a specific timeline with every estimate." },
    ]
  },
  {
    name: "Hinesville",
    slug: "hinesville",
    county: "Liberty County",
    state: "GA",
    population: "34,000",
    medianHomeValue: "$175,000",
    distanceFromHQ: "440",
    i75Position: 45,
    lat: 31.8468,
    lng: -81.5959,
    whyExpanding: "Hinesville is the home of Fort Stewart, the largest Army installation east of the Mississippi. With 34,000 residents — many military families — the community needs reliable roofing contractors who understand the urgency of PCS timelines and military family needs.",
    challenges: "Hinesville's proximity to the coast means tropical storm exposure, while its inland position also brings severe thunderstorms. Military families on tight timelines need fast, reliable roof work for home sales and purchases.",
    faqs: [
      { question: "Do you serve Hinesville and Fort Stewart?", answer: "Yes! We serve all of Hinesville and Liberty County, including military housing communities near Fort Stewart." },
      { question: "Do you offer military discounts?", answer: "Yes. We proudly support Fort Stewart families with special pricing for active duty, veterans, and military families." },
      { question: "Can you expedite work for PCS moves?", answer: "Yes. We understand military timelines and offer expedited scheduling for families buying or selling homes on PCS orders." },
    ]
  },
  {
    name: "Vidalia",
    slug: "vidalia",
    county: "Toombs County",
    state: "GA",
    population: "10,500",
    medianHomeValue: "$120,000",
    distanceFromHQ: "380",
    i75Position: 46,
    lat: 32.2177,
    lng: -82.4135,
    whyExpanding: "Vidalia, famous for its sweet onions, is the commercial center of Toombs County. This hardworking agricultural community values honest service and fair pricing — the same principles that have built O'Brien Contracting's reputation.",
    challenges: "Vidalia's southeast Georgia location brings intense summer heat, heavy rainfall, and the tail end of tropical systems. Agricultural buildings and residential properties both need durable, weather-resistant roofing solutions.",
    faqs: [
      { question: "Do you serve Vidalia?", answer: "Yes! Vidalia is part of our east Georgia expansion. We provide complete roofing services throughout Toombs County." },
      { question: "Can you roof agricultural buildings?", answer: "Yes. We install metal roofing on barns, processing facilities, and agricultural structures throughout the Vidalia area." },
      { question: "What's the most cost-effective option?", answer: "Architectural shingles starting around $380/square offer the best initial value. Metal roofing provides better long-term value with its 50+ year lifespan." },
    ]
  },
  {
    name: "Dublin",
    slug: "dublin",
    county: "Laurens County",
    state: "GA",
    population: "16,000",
    medianHomeValue: "$115,000",
    distanceFromHQ: "390",
    i75Position: 47,
    lat: 32.5404,
    lng: -82.9040,
    whyExpanding: "Dublin is Laurens County's seat and a regional center for healthcare and commerce in central-east Georgia. Our expansion brings professional roofing services to a community that has historically had limited access to licensed, insured contractors.",
    challenges: "Dublin's central Georgia location puts it in severe weather corridors. Tornadoes, hail, and straight-line winds are annual threats. Many homes have aging roofs that need full replacement rather than continued repairs.",
    faqs: [
      { question: "Do you serve Dublin and Laurens County?", answer: "Yes! Dublin is within our expanded Georgia coverage. We offer free estimates and full roofing services for Laurens County homeowners." },
      { question: "How do I know if my roof needs replacing?", answer: "Signs include missing shingles, leaks, sagging, granule loss, or age over 20 years. We offer free inspections to assess your roof's condition." },
      { question: "Do you offer emergency repairs?", answer: "Yes. We respond to emergency calls with tarping and temporary repairs to protect your home until permanent work can be scheduled." },
    ]
  },
  {
    name: "Milledgeville",
    slug: "milledgeville",
    county: "Baldwin County",
    state: "GA",
    population: "19,000",
    medianHomeValue: "$145,000",
    distanceFromHQ: "440",
    i75Position: 48,
    lat: 33.0801,
    lng: -83.2321,
    whyExpanding: "Milledgeville is Georgia's former capital and home to Georgia College & State University. Its rich history and growing student population create demand for roofing services that respect historic architecture while serving modern needs.",
    challenges: "Milledgeville's antebellum and Victorian architecture requires specialized roofing approaches. The area also experiences severe thunderstorms and ice events that challenge both historic and modern roofing systems.",
    faqs: [
      { question: "Do you serve Milledgeville?", answer: "Yes! Milledgeville is within our central-east Georgia coverage. We serve residential and commercial properties throughout Baldwin County." },
      { question: "Can you work on historic properties?", answer: "Yes. We have experience with historic homes and can recommend materials that maintain architectural character while providing modern protection." },
      { question: "What warranty do you provide?", answer: "Every installation includes our Lifetime Workmanship Guarantee plus manufacturer warranties of 25-50 years depending on material selected." },
    ]
  },
  {
    name: "Waycross",
    slug: "waycross",
    county: "Ware County",
    state: "GA",
    population: "13,700",
    medianHomeValue: "$95,000",
    distanceFromHQ: "310",
    i75Position: 49,
    lat: 31.2136,
    lng: -82.3540,
    whyExpanding: "Waycross is the gateway to the Okefenokee Swamp and Ware County's commercial center. Our expansion into south-east Georgia brings affordable, quality roofing to a community that values reliability and fair prices.",
    challenges: "Waycross's proximity to the Okefenokee creates extremely high humidity conditions that accelerate roof degradation. Tropical storms and severe thunderstorms are frequent threats requiring durable, weather-resistant materials.",
    faqs: [
      { question: "Do you serve Waycross?", answer: "Yes! Waycross is part of our south Georgia expansion. We offer complete roofing services with free estimates throughout Ware County." },
      { question: "How does humidity affect roofing here?", answer: "The Okefenokee proximity creates intense humidity. We use moisture-barrier underlayments and ensure proper ventilation to prevent mold, rot, and premature material failure." },
      { question: "What's the most durable option?", answer: "Metal roofing offers the best durability in Waycross — resistant to moisture, wind, and heat with a 50+ year lifespan." },
    ]
  },
  {
    name: "Douglas",
    slug: "douglas",
    county: "Coffee County",
    state: "GA",
    population: "11,600",
    medianHomeValue: "$105,000",
    distanceFromHQ: "330",
    i75Position: 50,
    lat: 31.5088,
    lng: -82.8499,
    whyExpanding: "Douglas is Coffee County's seat and a south Georgia hub for agriculture and small business. Our expansion brings the same licensed, guaranteed roofing services we've built our reputation on to this hardworking community.",
    challenges: "Douglas sees severe thunderstorms, occasional tornadoes, and intense summer heat. Many homes and businesses have roofs approaching end-of-life that need professional evaluation and replacement.",
    faqs: [
      { question: "Do you serve Douglas?", answer: "Yes! Douglas is within our south Georgia service area. We provide residential and commercial roofing throughout Coffee County." },
      { question: "Do you offer financing?", answer: "Yes. We offer flexible financing options to help Douglas homeowners afford quality roofing without compromising on materials or workmanship." },
      { question: "How quickly can you respond to storm damage?", answer: "We prioritize storm damage calls and can typically have a crew on-site within 48-72 hours for emergency assessment and tarping." },
    ]
  },
  {
    name: "Jesup",
    slug: "jesup",
    county: "Wayne County",
    state: "GA",
    population: "10,200",
    medianHomeValue: "$110,000",
    distanceFromHQ: "370",
    i75Position: 51,
    lat: 31.5988,
    lng: -81.8854,
    whyExpanding: "Jesup is Wayne County's seat and a crossroads community in southeast Georgia. Our expansion brings reliable roofing services to homeowners who have traditionally had few options for licensed, insured contractors.",
    challenges: "Jesup's location between the coast and inland Georgia means exposure to both tropical storms and severe thunderstorms. The area's pine forests create debris concerns, and high humidity demands moisture-resistant roofing solutions.",
    faqs: [
      { question: "Do you serve Jesup and Wayne County?", answer: "Yes! We serve all of Jesup and Wayne County with full roofing services including metal, shingle, and flat roof installations." },
      { question: "What about tree debris damage?", answer: "Pine tree debris is a common issue in Wayne County. We offer damage assessment, repairs, and can recommend trimming strategies to protect your roof long-term." },
      { question: "Do you handle insurance claims?", answer: "Yes. We assist with storm damage documentation, adjuster coordination, and ensure fair claim settlements for Jesup homeowners." },
    ]
  },
  {
    name: "Swainsboro",
    slug: "swainsboro",
    county: "Emanuel County",
    state: "GA",
    population: "7,300",
    medianHomeValue: "$95,000",
    distanceFromHQ: "390",
    i75Position: 52,
    lat: 32.5974,
    lng: -82.3340,
    whyExpanding: "Swainsboro is Emanuel County's seat and home to East Georgia State College. Our expansion brings quality roofing options to this growing community where reliable contractors have been in short supply.",
    challenges: "Swainsboro's central-east Georgia location exposes it to severe weather from multiple directions. Tornado activity and hail are significant seasonal threats that require impact-resistant roofing solutions.",
    faqs: [
      { question: "Do you serve Swainsboro?", answer: "Yes! Swainsboro is part of our east Georgia expansion. We provide complete roofing services throughout Emanuel County." },
      { question: "What impact-resistant options do you offer?", answer: "We install Class 4 impact-resistant shingles and standing seam metal roofing — both excellent choices for Swainsboro's hail-prone climate." },
      { question: "Do you offer free estimates?", answer: "Yes. We provide free, no-obligation estimates for all Swainsboro and Emanuel County properties." },
    ]
  },
  {
    name: "Sandersville",
    slug: "sandersville",
    county: "Washington County",
    state: "GA",
    population: "5,600",
    medianHomeValue: "$100,000",
    distanceFromHQ: "420",
    i75Position: 53,
    lat: 32.9821,
    lng: -82.8104,
    whyExpanding: "Sandersville is the 'Kaolin Capital of the World' and Washington County's seat. This mining and agricultural community deserves access to quality roofing services backed by professional expertise and genuine workmanship guarantees.",
    challenges: "Sandersville's kaolin mining history has created unique soil conditions. Severe thunderstorms and occasional ice events challenge roofing systems, and many older homes need modernization.",
    faqs: [
      { question: "Do you serve Sandersville?", answer: "Yes! Sandersville is within our central-east Georgia coverage. We offer free estimates and full roofing services for Washington County homeowners." },
      { question: "What's the best value option?", answer: "Architectural shingles provide excellent value starting around $380/square. For long-term investment, metal roofing's 50+ year lifespan offers the lowest cost per year." },
      { question: "Do you handle commercial properties?", answer: "Yes. We serve Sandersville businesses with commercial roofing including flat roof systems, TPO, and metal panels." },
    ]
  },
  {
    name: "Toccoa",
    slug: "toccoa",
    county: "Stephens County",
    state: "GA",
    population: "8,500",
    medianHomeValue: "$140,000",
    distanceFromHQ: "600",
    i75Position: 54,
    lat: 34.5773,
    lng: -83.3324,
    whyExpanding: "Toccoa sits in the foothills of the Blue Ridge Mountains, offering stunning natural beauty and a community that values quality. Our expansion into northeast Georgia brings professional roofing expertise to Stephens County homeowners.",
    challenges: "Toccoa's mountain proximity brings heavier snowfall and ice than most of Georgia, creating freeze-thaw stress on roofing materials. Steep-slope roofs are common and require specialized installation techniques.",
    faqs: [
      { question: "Do you serve Toccoa?", answer: "Yes! Toccoa is part of our northeast Georgia expansion. We provide full roofing services throughout Stephens County." },
      { question: "How do ice and snow affect roofing here?", answer: "Toccoa sees more winter weather than southern Georgia. We install ice and water shield on eaves, proper drip edge, and materials rated for freeze-thaw cycles." },
      { question: "Can you work on steep-slope roofs?", answer: "Yes. Our crews are trained in steep-slope techniques common in mountain foothill communities. Safety equipment and specialized methods ensure quality installations." },
    ]
  },
  {
    name: "Elberton",
    slug: "elberton",
    county: "Elbert County",
    state: "GA",
    population: "4,500",
    medianHomeValue: "$120,000",
    distanceFromHQ: "550",
    i75Position: 55,
    lat: 34.1115,
    lng: -82.8668,
    whyExpanding: "Elberton is the 'Granite Capital of the World' and a community built on craftsmanship. Our expansion into Elbert County brings the same dedication to quality that this stone-working community values in every project.",
    challenges: "Elberton's northeast Georgia location means more winter weather than the southern part of the state. Hail damage from severe spring storms is a recurring concern, and many homes have aging roofs needing full replacement.",
    faqs: [
      { question: "Do you serve Elberton?", answer: "Yes! Elberton is within our northeast Georgia service area. We offer free estimates and complete roofing services for Elbert County homeowners." },
      { question: "What's the best hail-resistant option?", answer: "Class 4 impact-resistant shingles and standing seam metal are the top choices for Elberton's hail-prone climate. Both can qualify for insurance discounts." },
      { question: "Do you handle ice damage?", answer: "Yes. We respond to ice and winter storm damage with emergency repairs and can install preventive measures like ice and water shield for long-term protection." },
    ]
  },
];

export const GEORGIA_SERVICE_AREAS = [
  // Original counties
  { name: "Spalding County", slug: "spalding-county", state: "GA" as const, description: "Griffin" },
  { name: "Pike County", slug: "pike-county", state: "GA" as const, description: "Zebulon" },
  { name: "Lamar County", slug: "lamar-county", state: "GA" as const, description: "Barnesville" },
  { name: "Monroe County", slug: "monroe-county", state: "GA" as const, description: "Forsyth" },
  { name: "Bibb County", slug: "bibb-county", state: "GA" as const, description: "Macon" },
  { name: "Houston County", slug: "houston-county", state: "GA" as const, description: "Warner Robins, Perry" },
  { name: "Crisp County", slug: "crisp-county", state: "GA" as const, description: "Cordele" },
  { name: "Tift County", slug: "tift-county", state: "GA" as const, description: "Tifton" },
  { name: "Lowndes County", slug: "lowndes-county", state: "GA" as const, description: "Valdosta, Lake Park" },
  { name: "Columbia County", slug: "columbia-county-fl", state: "FL" as const, description: "Lake City" },
  // Expansion counties
  { name: "Butts County", slug: "butts-county", state: "GA" as const, description: "Jackson" },
  { name: "Upson County", slug: "upson-county", state: "GA" as const, description: "Thomaston" },
  { name: "Peach County", slug: "peach-county", state: "GA" as const, description: "Fort Valley" },
  { name: "Dooly County", slug: "dooly-county", state: "GA" as const, description: "Vienna" },
  { name: "Pulaski County", slug: "pulaski-county", state: "GA" as const, description: "Hawkinsville" },
  { name: "Dodge County", slug: "dodge-county", state: "GA" as const, description: "Eastman" },
  { name: "Bleckley County", slug: "bleckley-county", state: "GA" as const, description: "Cochran" },
  { name: "Muscogee County", slug: "muscogee-county", state: "GA" as const, description: "Columbus" },
  { name: "Sumter County", slug: "sumter-county", state: "GA" as const, description: "Americus" },
  { name: "Dougherty County", slug: "dougherty-county", state: "GA" as const, description: "Albany" },
  { name: "Terrell County", slug: "terrell-county", state: "GA" as const, description: "Dawson" },
  { name: "Lee County", slug: "lee-county-ga", state: "GA" as const, description: "Leesburg" },
  { name: "Turner County", slug: "turner-county", state: "GA" as const, description: "Ashburn" },
  { name: "Ben Hill County", slug: "ben-hill-county", state: "GA" as const, description: "Fitzgerald" },
  { name: "Worth County", slug: "worth-county", state: "GA" as const, description: "Sylvester" },
  { name: "Colquitt County", slug: "colquitt-county", state: "GA" as const, description: "Moultrie" },
  { name: "Cook County", slug: "cook-county", state: "GA" as const, description: "Adel" },
  { name: "Berrien County", slug: "berrien-county", state: "GA" as const, description: "Nashville" },
  { name: "Mitchell County", slug: "mitchell-county", state: "GA" as const, description: "Camilla" },
  { name: "Thomas County", slug: "thomas-county", state: "GA" as const, description: "Thomasville" },
  { name: "Decatur County", slug: "decatur-county", state: "GA" as const, description: "Bainbridge" },
  { name: "Grady County", slug: "grady-county", state: "GA" as const, description: "Cairo" },
  { name: "Brooks County", slug: "brooks-county", state: "GA" as const, description: "Quitman" },
  { name: "Seminole County", slug: "seminole-county", state: "GA" as const, description: "Donalsonville" },
  { name: "Early County", slug: "early-county", state: "GA" as const, description: "Blakely" },
  { name: "Taylor County", slug: "taylor-county", state: "GA" as const, description: "Butler" },
  { name: "Wilcox County", slug: "wilcox-county", state: "GA" as const, description: "Abbeville" },
  { name: "Irwin County", slug: "irwin-county", state: "GA" as const, description: "Ocilla" },
  // East Georgia counties
  { name: "Chatham County", slug: "chatham-county", state: "GA" as const, description: "Savannah" },
  { name: "Richmond County", slug: "richmond-county", state: "GA" as const, description: "Augusta" },
  { name: "Clarke County", slug: "clarke-county", state: "GA" as const, description: "Athens" },
  { name: "Bulloch County", slug: "bulloch-county", state: "GA" as const, description: "Statesboro" },
  { name: "Liberty County", slug: "liberty-county", state: "GA" as const, description: "Hinesville" },
  { name: "Toombs County", slug: "toombs-county", state: "GA" as const, description: "Vidalia" },
  { name: "Laurens County", slug: "laurens-county", state: "GA" as const, description: "Dublin" },
  { name: "Baldwin County", slug: "baldwin-county", state: "GA" as const, description: "Milledgeville" },
  { name: "Ware County", slug: "ware-county", state: "GA" as const, description: "Waycross" },
  { name: "Coffee County", slug: "coffee-county", state: "GA" as const, description: "Douglas" },
  { name: "Wayne County", slug: "wayne-county", state: "GA" as const, description: "Jesup" },
  { name: "Emanuel County", slug: "emanuel-county", state: "GA" as const, description: "Swainsboro" },
  { name: "Washington County", slug: "washington-county", state: "GA" as const, description: "Sandersville" },
  { name: "Stephens County", slug: "stephens-county", state: "GA" as const, description: "Toccoa" },
  { name: "Elbert County", slug: "elbert-county", state: "GA" as const, description: "Elberton" },
];

// Get nearby cities using Haversine distance (works for cities off I-75)
export function getNearbyCities(currentSlug: string, count: number = 4): GeorgiaCity[] {
  const current = GEORGIA_CITIES.find(c => c.slug === currentSlug);
  if (!current) return GEORGIA_CITIES.slice(0, count);

  return GEORGIA_CITIES
    .filter(c => c.slug !== currentSlug)
    .map(c => ({ city: c, dist: haversineDistance(current.lat, current.lng, c.lat, c.lng) }))
    .sort((a, b) => a.dist - b.dist)
    .slice(0, count)
    .map(c => c.city);
}
